import React, { useState } from 'react'
import {
  Box,
  IconButton,
  Tooltip,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import EsimAPI from 'api/faber/EsimAPI'
import NormalText from 'common/Typography/NormalText'
import { format } from 'date-fns'
import { TransformedGlobalUserCdrData } from 'types/api/faber/globalCustomers.type'

import VoiceCallStatusLogsDialog, { VoiceCallStatusLogs } from '../dialogs/VoiceCallStatusLogs'

interface VoiceCallLog {
  userID: string;
  callSid: string;
  statusLogs: VoiceCallStatusLogs[];
  createdAt: string;
  updatedAt: string;
  from: string;
  to: string;
  duration: number;
  durationInMinutes: number;
  direction: string;
}

const columns = (onViewStatusLogs: (row: VoiceCallLog) => void) =>  [
  {
    field: 'callSid',
    headerName: 'Call SID',
    sortable: false,
    flex: 1,
    minWidth: 320, 
  },
  {
    field: 'from',
    headerName: 'From',
    sortable: false,
    flex: 1,   
    minWidth: 160,
  },
  {
    field: 'to',
    headerName: 'To',
    sortable: false,
    flex: 1,    
    minWidth: 160,
  },
  {
    field: 'durationInMinutes',
    headerName: 'Duration',
    sortable: true,
    width: 160,
    valueFormatter: ({ value }) => `${value} mins`
  },
  {
    field: 'createdAt',
    headerName: 'Date',
    sortable: false,
    width: 160,
    valueFormatter: ({ value }) =>
      value ? format(new Date(value), 'dd MMM yyyy HH:mm') : ''
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    width: 160,
    renderCell: (params: {row: VoiceCallLog}) => {
      const handleViewStatusLogs = () => {
        onViewStatusLogs(params.row);
      };

      return (
        <Box>
          <Tooltip title={'View status logs'}>
            <IconButton
              color="primary"
              onClick={handleViewStatusLogs}
              sx={{
                padding: 1,
                backgroundColor: 'blue.50',
                '&:hover': {
                  backgroundColor: 'blue.100',
                },
              }}
            >
              <NormalText
                sx={{
                  paddingY: 0.5,
                  paddingX: 1,
                  border: 1,
                  borderStyle: 'solid',
                  borderRadius: 4,
                  borderColor: 'blue',
                  color: 'blue',
                }}
              >
                View Status Logs
              </NormalText>
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
  }
]

function VoiceCallLogs({ faberToken, customerRow, expanded }: VoiceCallLogsProps) {
  const [selectedVoiceLog, setSelectedVoiceLog] = useState<VoiceCallLog | null>(null);
  const [viewStatusLogDialog, setViewStatusLogDialog] = useState<boolean>(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const userId = customerRow.userID;

  const { data, isLoading } = useQuery(
    ['voiceCallLogs', userId, page, pageSize],
    async () => {
      const response = await EsimAPI.getUserVoiceCallLogs({
        faberToken,
        userId,
        page: page + 1,
        pageSize,
      });
      return response.data.data;
    },
    {
      enabled: !!expanded && !!userId,
      keepPreviousData: true,
    }
  );

  const onViewStatusLogs = (row: VoiceCallLog) => {
    setViewStatusLogDialog(true);
    setSelectedVoiceLog(row);
  };

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <DataGrid
        rows={data?.logs ?? []}
        columns={columns(onViewStatusLogs)}
        loading={isLoading}
        getRowId={(row) => `${row.createdAt}-${row.callSid}`}
        rowCount={data?.total ?? 0}
        pagination
        paginationMode="server"
        page={page}
        pageSize={pageSize}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        disableSelectionOnClick
        disableColumnMenu
        style={{
          border: 'none',
        }}
      />

      {selectedVoiceLog && (
        <VoiceCallStatusLogsDialog
          onClose={() => setViewStatusLogDialog(false)}
          open={viewStatusLogDialog}
          statusLogs={selectedVoiceLog.statusLogs}
        />
      )}
    </Box>
  );
}

export default VoiceCallLogs

interface VoiceCallLogsProps {
  faberToken: string
  customerRow: TransformedGlobalUserCdrData
  expanded: string | false
}
